import { Injectable } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { UtilityService } from 'src/app/core/services/utility.service';
import { ToggleHide, ToggleShow } from 'src/app/shared/ui/spinner/redux/spinner.actions';
import { MapsService } from '../services/maps.service';
import { RobotsService } from '../services/robots.service';
import { QaStateModel } from './qa-state.model';
import {
  DeleteRobots,
  DeleteRobotsSuccess,
  LoadAllRobots,
  LoadOwnerRobots,
  LoadOwnerRobotsSuccess,
  LoadRobotMapRegionsSuccess,
  LoadRobotMapsSuccess,
  LoadRobotMapVersionsSuccess,
  LoadRobotsSuccess,
  PostRobots,
  QAPostNotificationFail,
  QAPostNotificationSuccess,
  ReloadAllRobots,
  ReloadOwnerRobots,
  ReloadRobotsSuccess,
  SaveRobotComplete,
  SaveRobotFail
} from './qa.actions';

@Injectable()
@State<QaStateModel>({
  name: 'qa',
  defaults: {
    Robots: [],
    ownerRobots: undefined,
    error: null,
    robotMaps: [],
    robotMapVersions: [],
    robotMapRegions: []
  }
})
export class QaState {
  constructor(
    private robotService: RobotsService,
    private toastrService: NbToastrService,
    private utilityService: UtilityService,
    private mapsService: MapsService
  ) {}

  /*******************************************
   *                 ROBOTS
   ******************************************/

  @Selector()
  public static robots(state: QaStateModel) {
    return state.Robots;
  }

  @Selector()
  public static ownerRobots(state: QaStateModel) {
    return state.ownerRobots;
  }

  @Selector()
  public static robotMaps(state: QaStateModel) {
    return state.robotMaps;
  }

  @Selector()
  public static robotMapVersions(state: QaStateModel) {
    return state.robotMapVersions;
  }

  @Selector()
  public static robotMapRegions(state: QaStateModel) {
    return state.robotMapRegions;
  }

  /******************************************/

  @Action(LoadAllRobots)
  public async loadAllRobots(ctx: StateContext<QaStateModel>) {
    ctx.dispatch(ToggleShow);
    this.robotService.loadOwnerRobotsAmplify();
    this.robotService.loadAllRobotsAmplify();
  }

  @Action(LoadOwnerRobots)
  public async loadOwnerRobots(ctx: StateContext<QaStateModel>) {
    ctx.dispatch(ToggleShow);
    this.robotService.loadOwnerRobotsAmplify();
  }

  @Action(LoadOwnerRobotsSuccess)
  public async loadOwnerRobotsSuccess(ctx: StateContext<QaStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      ownerRobots: payload
    });
    ctx.dispatch(ToggleHide);
  }

  @Action(LoadRobotsSuccess)
  public loadRobotsSuccess(ctx: StateContext<QaStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      Robots: payload
    });
    ctx.dispatch(ToggleHide);
  }

  @Action(ReloadAllRobots)
  public async reloadAllRobots(ctx: StateContext<QaStateModel>) {
    this.toastrService.show(status, $localize`Fetch Initiated`, { status: 'info' });
    this.robotService.loadAllRobotsAmplify(true);
  }

  @Action(ReloadOwnerRobots)
  public async reloadOwnerRobots(ctx: StateContext<QaStateModel>) {
    this.robotService.loadOwnerRobotsAmplify();
  }

  @Action(ReloadRobotsSuccess)
  public reloadRobotsSuccess(ctx: StateContext<QaStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      Robots: payload
    });
  }

  @Action(PostRobots)
  public async postRobots(ctx: StateContext<QaStateModel>, { payload, edit }: any) {
    this.robotService.postRobotAmplify(payload, edit);
  }

  @Action(DeleteRobots)
  public deleteRobots(ctx: StateContext<QaStateModel>, { payload }: any) {
    this.robotService.deleteRobotAmplify(payload);
  }

  @Action(DeleteRobotsSuccess)
  public deleteRobotsSucess(ctx: StateContext<QaStateModel>, { payload }: any) {
    if (payload.deleted === true) {
      ctx.setState(
        patch({
          // @ts-ignore
          ownerRobots: removeItem((item) => item.id === payload.id),
          // @ts-ignore
          Robots: removeItem((item) => item.id === payload.id)
        })
      );
      this.toastrService.show(status, $localize`Delete: Complete`, { status: 'success' });
    }
  }

  @Action(SaveRobotComplete)
  public robotsComplete(ctx: StateContext<QaStateModel>, { data, robots }: any) {
    const state = ctx.getState();
    if (data === true) {
      ctx.setState(
        patch({
          ownerRobots: updateItem((item) => item.id === robots.id, robots)
        })
      );
      this.toastrService.show(status, $localize`Edit: Complete`, { status: 'success' });
    } else {
      ctx.setState(
        patch({
          // @ts-ignore
          ownerRobots: [...state.ownerRobots, robots],
          Robots: [...state.Robots, robots]
        })
      );

      this.toastrService.show(status, $localize`Save: Complete`, { status: 'success' });
    }
  }

  @Action(SaveRobotFail)
  public robotsFail(ctx: StateContext<QaStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      error: payload
    });
    this.toastrService.show(status, $localize`Save: Failed: ${payload.statusText}`, { status: 'danger' });
  }

  /*******************************************
   *          QA Tool Notification Test
   ******************************************/
  @Action(QAPostNotificationSuccess)
  public async postNotificationSuccess(ctx: StateContext<QaStateModel>) {
    this.toastrService.show(status, $localize`Submit: Complete`, { status: 'success' });
  }

  @Action(QAPostNotificationFail)
  public async postNotificitaionFail(ctx: StateContext<QaStateModel>, { payload }: any) {
    this.toastrService.show(status, $localize`Submit: Failed: ${this.utilityService.convertErrorToMessage(payload)}`, {
      status: 'danger',
      duration: 6000
    });
  }

  /*******************************************
   *          Favorite Recommendation
   ******************************************/
  @Action(LoadRobotMapsSuccess)
  public async robotMapLoadSuccess(ctx: StateContext<QaStateModel>, { robotMaps }: any) {
    // const state = ctx.getState();
    ctx.setState(
      patch({
        robotMaps: insertItem(robotMaps)
      })
    );
  }

  @Action(LoadRobotMapVersionsSuccess)
  public async robotMapVersionsLoadSuccess(ctx: StateContext<QaStateModel>, { rmv }: any) {
    const state = ctx.getState();
    ctx.setState(
      patch({
        robotMapVersions: insertItem(rmv)
      })
    );
  }

  @Action(LoadRobotMapRegionsSuccess)
  public async robotMapRegionsLoadSuccess(ctx: StateContext<QaStateModel>, { regions }: any) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      robotMapRegions: regions
    });
  }
}
